/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/FlexPanelPage';
import { preventIfParentPathContainsAutoLayout } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  Item,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { cssGridRenderAs } from '../../registry/constants';
import { defaultValue } from './FlexPanel';
import { genId } from 'designer/state';

const name = 'Flex Panel';

const FlexPanelManager: ComponentManagerFn<'FlexPanel'> = (
  platform: Platform,
): ComponentManager<'FlexPanel'> => {
  return {
    ...baseComponentManager<'FlexPanel'>(platform, 'FlexPanel'),
    defaultValue,
    defaultChildren: ['FlexItem', 'FlexItem'],
    isChildAllowed: (page, item, childType) =>
      (['FlexItem'] as ItemType[]).includes(childType),
    isAllowedInsideParent: (page, item) =>
      preventIfParentPathContainsAutoLayout(page, item, name),
    props: {
      renderAs: {
        label: 'Render As',
        type: 'select',
        options: cssGridRenderAs,
        required: true,
        helpText:
          'Choose rows for Vertial Layout or columns for Horizontal Layout.',
      },
      toggleBelowWidth: {
        label: 'Toggle Below Width',
        type: 'numberInput',
        helpText: 'Toggle to a single column below this width.',
      },
      gridGap: {
        label: 'Grid Gap',
        type: 'number',
        min: 0,
        max: 40,
        step: 4,
        helpText: 'Specify the gap in pixels between child components. e.g. 8',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        helpText: 'Defines the number of grids the component is going to use.',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Look & Feel',
      },
      backgroundColor: {
        label: 'BG Color',
        type: 'BGColor',
        category: 'Look & Feel',
        allowCustomColor: true,
      },
    },
    name,
    category: 'Layout',
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'expand-wide' },
    isPropertyVisible: (property, item) => {
      if (property === 'toggleBelowWidth') {
        return item.renderAs === 'columns';
      }
      return true;
    },
    replaceableWith: (item) =>
      item.children.length > 2
        ? []
        : (item as Item<'FlexPanel'>).props.renderAs === 'rows'
        ? ['HorizontalSplitPanel']
        : ['VerticalSplitPanel'],
    onReplace: (
      item: Item<'FlexPanel'>,
      fromItem: Item<'HorizontalSplitPanel'> | Item<'VerticalSplitPanel'>,
      page,
    ) => {
      const children: string[] = [];
      fromItem.children.forEach((childId, index) => {
        const itemId = genId(page);
        const flexItem: Item<'FlexItem'> = {
          type: 'FlexItem',
          props: { sizeType: 'flex' },
          children: [childId],
          itemId,
          parent: item.itemId,
        };
        if (
          fromItem.type === 'HorizontalSplitPanel' &&
          fromItem.props.fixedHeight
        ) {
          item.props.renderAs = 'rows';
          if (
            (fromItem.props.fixedSide === 'start' && index === 0) ||
            (fromItem.props.fixedSide === 'end' && index === 1)
          ) {
            flexItem.props.sizeType = 'minmax';
            flexItem.props.min = fromItem.props.fixedHeight;
            flexItem.props.max = fromItem.props.fixedHeight;
          }
        }
        if (
          fromItem.type === 'VerticalSplitPanel' &&
          fromItem.props.fixedWidth
        ) {
          item.props.renderAs = 'columns';
          if (
            (fromItem.props.fixedSide === 'start' && index === 0) ||
            (fromItem.props.fixedSide === 'end' && index === 1)
          ) {
            flexItem.props.sizeType = 'minmax';
            flexItem.props.min = fromItem.props.fixedWidth;
            flexItem.props.max = fromItem.props.fixedWidth;
          }
        }
        page[childId] = { ...page[childId], parent: itemId };
        page[itemId] = flexItem;
        children.push(itemId);
      });
      item.children = children;
      return item;
    },
    description: `Flex Panel is the basic building block for creating responsive layouts. It can be used to split the screen space for multiple child components to responsively occupy the entier screen space.

\`\`\`alert
Note: Flex Panel is a Fixed layout component. i.e. it can only be used inside other Fixed or Size Aware layout components.
\`\`\`
    

\`\`\`alert
Note: Flex Panels are primarily used to create scrollbar free screen layouts targetted for large screens like Desktops & Laptops.
\`\`\`

Let's consider the following layout example.

---

Smart Search

---

Data Grid

Row 1

Row 2

Row ...

---

In the above layout, the Smart Search should be able to grow as needed based on the number of filters applied by the user. And the Data Grid below should consume the rest of the space available on the page.

This can be achived using Flex Panel with two Flex Items, one with Size Type prop set to Auto for the Smart Search and Flex for the bottom Flex Item.

\`\`\`alert_success
Under the hood, it uses CSS Grid for the layouts.
\`\`\`

Refer to [CSS Grid](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout) for more information on CSS Grid Layout
`,
    helpRenders: [
      {
        title: 'Flex Panel',
        description:
          'Flex Items can be rendered as Rows (one below the other) or Columns (next to each other)',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['renderAs'],
        item: {
          itemId: 'flexPanel',
          props: {
            renderAs: 'rows',
          },
          children: ['flexItem0', 'flexItem1', 'flexItem3', 'flexItem2'],
          type: 'FlexPanel',
          parent: 'root',
        },
      },
      {
        title: 'Flex Panel',
        exampleId: 'e2',
        description:
          'Use the Grid Gap property to specifiy the spacing between Flex Items',
        page: SamplePage,
        visibleProps: ['gridGap'],
        item: {
          itemId: 'flexPanel',
          props: {
            renderAs: 'columns',
            gridGap: 16,
          },
          children: ['flexItem0', 'flexItem1', 'flexItem3', 'flexItem2'],
          type: 'FlexPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default FlexPanelManager;
